
import { Component, Inject, Prop, Vue, Watch } from 'vue-property-decorator';
import GeneralScaffold from '../GeneralScaffold.vue';
import GlobalViewModel from '@/state/GlobalViewModel';
import GlossSearchResult from '@/repositories/data/GlossSearchResult';
import Theme from '@/repositories/data/Theme';
import VariantCategory from '@/repositories/data/VariantCategory';
import SearchThemeSelector from '@/components/SearchThemeSelector.vue';
import SearchVariantCategorySelector from '@/components/SearchVariantCategorySelector.vue';
import SearchSignParametersSelector from '@/components/SearchSignParametersSelector.vue';
import SearchOrderBySelector from '@/components/SearchOrderBySelector.vue';
import SignShape from '@/repositories/data/SignShape';
import SignGesture from '@/repositories/data/SignGesture';
import SignOralComponent from '@/repositories/data/SignOralComponent';
import SignLocation from '@/repositories/data/SignLocation';
import SignPropertySelection from '@/repositories/data/SignPropertySelection';
import Pagination from '@/components/Pagination.vue';
import MediaFileThumbnail from '@/components/MediaFileThumbnail.vue';
import GeolocationFilterSelector from '@/components/GeolocationFilterSelector.vue';
import { SignPropertyFilter } from './searchTypes';

@Component({
    components: {
        GeolocationFilterSelector,
        SearchSignParametersSelector,
        GeneralScaffold,
        SearchThemeSelector,
        SearchVariantCategorySelector,
        SearchOrderBySelector,
        Pagination,
        MediaFileThumbnail,
    },
})
export default class SearchScreen extends Vue {
    @Prop({ default: 'words' }) public searchMode!: string;
    @Prop({ default: true }) public referenceDataLoading!: boolean;
    @Prop({ default: () => [] }) public availableThemes!: Theme[];
    @Prop({ default: () => [] }) public availableVariantCategories!: VariantCategory[];
    @Prop({ default: () => [] }) public signShapes!: SignShape[];
    @Prop({ default: () => [] }) public signGestures!: SignGesture[];
    @Prop({ default: () => [] }) public signLocations!: SignLocation[];
    @Prop({ default: () => [] }) public signOralComponents!: SignOralComponent[];
    @Prop({ default: null }) public themeId!: string | null;
    @Prop({ default: '0' }) public variantCategoryId!: string;
    @Prop({
        default: () => ({
            shapeLeft: '',
            shapeRight: '',
            gestureLeft: '',
            gestureRight: '',
            location: '',
            oralComponent: '',
        }),
    })
    public signPropertySelection!: SignPropertySelection;
    @Prop({ default: 1 }) public currentPage!: number;
    @Prop({ default: 'name' }) public ordering!: string;
    @Prop({ default: null }) public moduleId!: string | null;
    @Prop({ default: () => [] }) public searchResults!: GlossSearchResult[];
    @Prop({ default: false }) public searchLoading!: boolean;
    @Prop({ default: '' }) public searchTextQueryBasis!: string;
    @Prop({ default: 0 }) public searchResultsTotal!: number;
    @Prop({ default: null }) public moduleName!: string | null;
    @Prop({ default: 0 }) public totalPageCount!: number;
    @Prop({ default: () => [] }) public activeSignPropertyFilters!: SignPropertyFilter[];
    @Prop({ default: () => ({}) }) public currentSearchQuery!: Record<string, string>;

    // Local UI state
    public filterMenuShown = false;
    public latestQuickSearchResult: GlossSearchResult | null = null;

    @Inject() private readonly globalViewModel!: GlobalViewModel;

    public get showingParameterSelection(): boolean {
        return this.searchMode === 'parameters';
    }

    public get showingGeolocation(): boolean {
        return this.searchMode === 'geolocation';
    }

    public mounted(): void {
        window.addEventListener('resize', this.resizeHandler);
    }

    public beforeDestroy(): void {
        window.removeEventListener('resize', this.resizeHandler);
    }

    public onSearchTextQuery(query: string): void {
        this.$emit('search-text-query', query);
    }

    public onShowGloss(gloss: GlossSearchResult): void {
        const params: any = { id: gloss.id };
        if (this.variantCategoryId && this.variantCategoryId !== '0') {
            params.category_id = this.variantCategoryId;
        }
        // _fromSearch=1 signals SearchContainer that this navigation came from SearchScreen
        // (underscored to avoid conflict with any other query parameters)
        this.$router.push({ name: 'gloss-details', params, query: { ...this.currentSearchQuery, _fromSearch: '1' } });
    }

    @Watch('filterMenuShown')
    public onFilterMenuShownChanged(value: boolean): void {
        this.globalViewModel.isMobileFullscreenModalOpen = value;
    }

    public onScreenResize(): void {
        if (window.innerWidth > 768) {
            this.filterMenuShown = false;
        }
    }

    private resizeHandler = (): void => {
        this.onScreenResize();
    };
}
